/**
 * FinanceReportPage — 149-cw-r119 (2026-08-29)
 *
 * Halaman "Laporan Keuangan" tunggal untuk konsolidasi data finansial ENS.
 *
 * Access: gate via `finance_report` di access matrix. TIDAK hardcode division/level.
 *
 * Tabs:
 *  1. Ringkasan       — Filter + ringkasan per proyek + panel detail
 *  2. Invoice & Piutang — daftar invoice + detail panel kanan
 *  3. Pembayaran Masuk — riwayat payment_receipts (flat across projects)
 *  4. Pengeluaran     — expense_entries + petty cash (unified read view)
 *  5. Retensi & Collection — ringkasan retensi + link ke Collection Page
 *  6. Laporan         — filter periode + preview ringkasan bulanan
 *
 * Semua tab READ-ONLY. Pembukuan resmi dilakukan di software akuntansi terpisah.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AlertTriangle, X, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import api from "@/lib/api";
import RingkasanTab from "@/components/finance-report/RingkasanTab";
import InvoicePiutangTab from "@/components/finance-report/InvoicePiutangTab";
import PengeluaranTab from "@/components/finance-report/PengeluaranTab";
import RetensiCollectionTab from "@/components/finance-report/RetensiCollectionTab";
import PphWithholdingTab from "@/components/finance-report/PphWithholdingTab";
import VerifikasiPenerimaanTab from "@/components/finance-report/VerifikasiPenerimaanTab";
import LaporanTab from "@/components/finance-report/LaporanTab";

const TABS = [
  { key: "ringkasan",          label: "Ringkasan" },
  { key: "invoice-piutang",    label: "Invoice & Piutang" },
  { key: "pengeluaran",        label: "Pengeluaran" },
  { key: "retensi-collection", label: "Retensi & Collection" },
  { key: "potongan-pph",       label: "Potongan PPh" },
  { key: "verifikasi-penerimaan", label: "Verifikasi Penerimaan" },
  { key: "laporan",            label: "Laporan" },
];

export default function FinanceReportPage() {
  const { user } = useAuth();
  const { canPerm, ready: accessReady } = useAccess();
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get("tab") && TABS.find((t) => t.key === params.get("tab"))?.key) || "ringkasan";
  const [tab, setTab] = useState(initialTab);
  const nav = useNavigate();
  // 2026-06-19 (user request): tombol kembali ke Dashboard Monitoring saat
  // datang dari monitoring project (?from=monitoring&project_id=).
  const fromMonitoring = params.get("from") === "monitoring";
  const [projects, setProjects] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [loading, setLoading] = useState(false);
  // 2026-06-21 (user report HTTP 520): status gagal muat — JANGAN pernah
  // menampilkan Rp0/"tidak ada proyek" seolah data hilang.
  const [loadError, setLoadError] = useState(null);
  // 149-cw-r138 (2026-08-31): Pre-filter proyek/WO via URL param (?project_id=).
  // Digunakan saat user klik "Laporan Keuangan →" dari Monitoring WO — supaya
  // konteks proyek tidak hilang & user tidak perlu cari WO lagi.
  const initialProjectId = params.get("project_id") || "";
  const [scopedProjectId, setScopedProjectId] = useState(initialProjectId);

  const canView = canPerm(user, "finance_report", "view");
  const canExport = canPerm(user, "finance_report", "export") || canPerm(user, "finance_report", "view");

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [projRes, pcRes] = await Promise.all([
        api.get("/active-projects"),
        api.get("/petty-cash").catch(() => ({ data: [] })),
      ]);
      setProjects(Array.isArray(projRes.data) ? projRes.data : []);
      setPettyCash(Array.isArray(pcRes.data) ? pcRes.data : (pcRes.data?.items || []));
    } catch (e) {
      console.warn("Load finance report failed", e);
      setLoadError("Server tidak merespons atau koneksi terputus.");
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("ens:refresh", handler);
    return () => window.removeEventListener("ens:refresh", handler);
  }, [load]);

  useEffect(() => {
    // Sync tab dgn ?tab= query param supaya tombol browser back/forward konsisten.
    const cur = params.get("tab");
    if (cur !== tab) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
  }, [tab, params, setParams]);

  // Flatten data for tabs: derive kali sekali di parent, kirim ke children (avoid duplikasi compute).
  const invoiceRows = useMemo(() => {
    const rows = [];
    for (const p of projects) {
      for (const e of (p.income_entries || [])) {
        rows.push({ ...e, __project: p });
      }
    }
    return rows;
  }, [projects]);

  const receiptRows = useMemo(() => {
    const rows = [];
    for (const r of invoiceRows) {
      for (const rc of (r.payment_receipts || [])) {
        rows.push({ ...rc, __entry: r, __project: r.__project });
      }
    }
    return rows;
  }, [invoiceRows]);

  const expenseRows = useMemo(() => {
    const rows = [];
    for (const p of projects) {
      for (const e of (p.expense_entries || [])) {
        if (e.excluded_from_totals === true) continue;
        rows.push({ ...e, __project: p, __source: "expense" });
      }
    }
    return rows;
  }, [projects]);

  const scopedProject = useMemo(
    () => projects.find((p) => p.id === scopedProjectId) || null,
    [projects, scopedProjectId],
  );
  const clearScope = () => {
    setScopedProjectId("");
    const next = new URLSearchParams(params);
    next.delete("project_id");
    setParams(next, { replace: true });
  };

  if (!accessReady) {
    return <div className="p-6 text-sm text-slate-400 italic">Memuat izin akses…</div>;
  }
  if (!canView) {
    return (
      <div className="p-6" data-testid="fin-report-no-access">
        <div className="max-w-md mx-auto mt-16 border border-rose-200 bg-rose-50 p-6 text-center">
          <AlertTriangle size={28} className="text-rose-600 mx-auto mb-2" />
          <h2 className="font-display text-lg font-bold text-rose-800 mb-1">Akses Ditolak</h2>
          <p className="text-xs text-rose-700 leading-relaxed">
            Anda belum punya izin untuk melihat <b>Laporan Keuangan</b>.
            Minta admin mengaktifkan permission <code className="font-mono bg-white/70 px-1">finance_report.view</code> di Access Control.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="finance-report-page">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Keuangan
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight mt-1 font-display">
          Laporan Keuangan
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Informasi keuangan proyek dan operasional perusahaan.
        </p>
      </div>

      {/* Tombol kembali ke Dashboard Monitoring project (bila datang dari sana) */}
      {fromMonitoring && scopedProjectId && (
        <button
          type="button"
          data-testid="fin-btn-back-monitoring"
          onClick={() => nav(`/projects/${scopedProjectId}/monitoring?tab=keuangan`)}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700 w-fit"
        >
          <ArrowLeft size={13} /> Kembali ke Dashboard Monitoring
        </button>
      )}

      {/* 149-cw-r138: Konteks scoping WO/Proyek dari URL param. Banner navy prominent
          supaya user tahu daftar sedang di-filter, dan bisa reset dgn 1 klik. */}
      {scopedProjectId && (
        <div
          data-testid="fin-report-scope-banner"
          className="flex items-center gap-3 border-l-4 border-[#FF5A1F] bg-[#0B1D3A] text-white px-4 py-2.5"
        >
          <Building2 size={16} className="text-[#FF5A1F] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-orange-200 font-semibold">
              Difilter ke {scopedProject?.project_type === "working_order" ? "Working Order" : "Proyek"}
            </div>
            <div className="text-sm font-semibold truncate">
              {scopedProject ? (
                <>
                  {scopedProject.wo_number ? `WO ${scopedProject.wo_number} · ` : ""}
                  {scopedProject.project_name || scopedProject.parent_project_name || "-"}
                  {scopedProject.po_number ? ` · PO ${scopedProject.po_number}` : ""}
                </>
              ) : (
                <span className="italic text-slate-300">Proyek belum ditemukan (mungkin sudah dihapus)</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={clearScope}
            data-testid="fin-report-scope-clear"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white shrink-0"
          >
            <X size={11} /> Reset Filter
          </button>
        </div>
      )}

      {/* Tabs — R5 navy active per mockup */}
      <div className="border-b border-slate-200 flex flex-wrap gap-1" role="tablist" data-testid="fin-report-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`fin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs font-semibold transition-colors -mb-px ${
              tab === t.key
                ? "bg-[#0B1D3A] text-white border border-[#0B1D3A]"
                : "text-slate-600 hover:text-[#0B1D3A] hover:bg-slate-100 border border-transparent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 2026-06-21 (user report HTTP 520): kegagalan memuat TIDAK tampil
          sebagai Rp0/"tidak ada proyek" — panel gagal + tombol Coba Lagi. */}
      {loadError && !loading && projects.length === 0 && (
        <div className="border border-rose-200 bg-rose-50 p-8 text-center" data-testid="finance-load-error">
          <AlertTriangle size={28} className="mx-auto text-rose-400 mb-3" />
          <div className="text-sm font-bold text-rose-700">Gagal memuat Laporan Keuangan</div>
          <div className="text-xs text-rose-600 mt-1 mb-4 max-w-md mx-auto">
            {loadError} Data Anda aman — ini masalah sementara pada koneksi/server, bukan data yang hilang.
          </div>
          <button type="button" onClick={load} data-testid="finance-retry-btn"
            className="px-4 py-2 text-xs font-semibold bg-[#0B1D3A] text-white hover:bg-[#12294f]">
            Coba Lagi
          </button>
        </div>
      )}
      {loadError && projects.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 px-3 py-2 flex items-center justify-between gap-2" data-testid="finance-stale-warning">
          <span className="text-[11px] text-amber-700 inline-flex items-center gap-1.5">
            <AlertTriangle size={12} /> Gagal memperbarui data — menampilkan data terakhir yang berhasil dimuat.
          </span>
          <button type="button" onClick={load} data-testid="finance-retry-stale-btn"
            className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 hover:text-[#FF5A1F] underline shrink-0">
            Coba Lagi
          </button>
        </div>
      )}

      {/* Tab content */}
      <div className={loadError && !loading && projects.length === 0 ? "hidden" : ""}>
        {tab === "ringkasan" && (
          <RingkasanTab
            projects={projects}
            invoiceRows={invoiceRows}
            receiptRows={receiptRows}
            expenseRows={expenseRows}
            pettyCash={pettyCash}
            loading={loading}
          />
        )}
        {tab === "invoice-piutang" && (
          <InvoicePiutangTab
            invoiceRows={invoiceRows}
            loading={loading}
            onRefresh={load}
            initialProjectId={scopedProjectId}
            projects={projects}
          />
        )}
        {tab === "pengeluaran" && (
          <PengeluaranTab expenseRows={expenseRows} pettyCash={pettyCash} loading={loading} projects={projects} onChanged={load} initialProjectId={scopedProjectId} />
        )}
        {tab === "retensi-collection" && (
          <RetensiCollectionTab projects={projects} invoiceRows={invoiceRows} loading={loading} />
        )}
        {tab === "potongan-pph" && (
          <PphWithholdingTab projects={projects} />
        )}
        {tab === "verifikasi-penerimaan" && (
          <VerifikasiPenerimaanTab />
        )}
        {tab === "laporan" && (
          <LaporanTab canExport={canExport} projects={projects} loading={loading} />
        )}
      </div>
    </div>
  );
}
